import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { LayoutDashboard } from 'lucide-react';
import AuraIcon from './AuraIcon.jsx';
import { useAuth } from '../../contexts/AuthContext';
import { Capacitor } from '@capacitor/core';

import { useLocation, useNavigate } from 'react-router-dom';
import NotificationBell from '../NotificationBell';
import Avatar from './Avatar.jsx';

const Header = ({
  currentUser,
  activeSessions = [],
  plannedSessions = [],
  onJoinSession,
  completedSessions = [],
  setCurrentView,
  rightContent,
  subtitle,
  disableAutoHide = true,
  position = 'sticky',
  topOffset = 0
}) => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { user: authUser, getUserRole } = useAuth();
  const userRole = getUserRole();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Der Knopf rechts ist ein Umschalter, kein Einbahnschild: In der App fuehrt
  // er ins Dashboard, im Dashboard zurueck in die App. Bis September 2026 stand
  // dort auch im Dashboard "Dashboard" — genau an der Stelle, an der ein neuer
  // Schul-Admin den Rueckweg gesucht hat, zeigte der Kopf sein Gegenteil.
  // Erkennung ueber den Pfad, nicht ueber eine Prop: Der Header wird an zwei
  // Stellen gerendert (MainScreenHeader, DashboardLayout), und bei Deep-Links
  // oder der Zurueck-Taste waere eine durchgereichte Prop nur dann richtig,
  // wenn der Aufrufer daran denkt.
  const inDashboard = pathname.startsWith('/dashboard');

  // Scroll detection für Header auto-hide (nur auf Mobile)
  useEffect(() => {
    if (disableAutoHide) {
      setIsHeaderVisible(true);
      return undefined;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Header ausblenden wenn runter scrollt (> 50px), anzeigen wenn hoch scrollt
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setIsHeaderVisible(false); // Hide when scrolling down
      } else if (currentScrollY < lastScrollY || currentScrollY <= 50) {
        setIsHeaderVisible(true); // Show when scrolling up or at top
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [disableAutoHide, lastScrollY]);

  // User data preparation (same as before)
  const getUserForComponents = () => {
    if (!currentUser || Object.keys(currentUser).length === 0) {
      return authUser || {};
    }

    const combinedUser = { ...currentUser };

    if (authUser) {
      if (!combinedUser.email && authUser.email) combinedUser.email = authUser.email;

      combinedUser.auraPoints = combinedUser.auraPoints ||
                                authUser.auraPoints ||
                                (authUser.auraStats?.currentPoints) ||
                                (combinedUser.auraStats?.currentPoints) || 0;

      combinedUser.auraLevel = combinedUser.auraLevel ||
                              authUser.auraLevel ||
                              (authUser.auraStats?.level) ||
                              (combinedUser.auraStats?.level) || 1;

      if (!combinedUser.auraStats && authUser.auraStats)
        combinedUser.auraStats = authUser.auraStats;
      else if (!combinedUser.auraStats)
        combinedUser.auraStats = { currentPoints: combinedUser.auraPoints, level: combinedUser.auraLevel };

      if (!combinedUser.profile && authUser.profile) combinedUser.profile = authUser.profile;
    }

    if (typeof combinedUser.auraPoints === 'undefined') {
      combinedUser.auraPoints = 0;
    }

    if (typeof combinedUser.auraLevel === 'undefined') {
      combinedUser.auraLevel = 1;
    }

    return combinedUser;
  };

  const combinedUser = getUserForComponents();

  // 🆕 Navigate to AccountSettingsPage instead of modal (24. Okt 2025)
  const handleAvatarClick = useCallback(() => {
    navigate('/account-settings');
  }, [navigate]);

  const displayAuraPoints = combinedUser.auraPoints || 0;
  const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

  // Mini-Profilbild: bereits im AuthContext aufgeloest + normalisiert (image/avatar)
  const avatarUrl = combinedUser.image || combinedUser.avatar || '';
  const displayName = combinedUser.name || combinedUser.displayName || combinedUser.profile?.displayName || '';

  // Rollenbasiertes Dashboard-Ziel (nur fuer Lehr-/Verwaltungsrollen)
  const dashboardPath = userRole === 'SUPERADMIN'
    ? '/dashboard/super-admin'
    : userRole === 'ADMIN'
      ? '/dashboard/admin'
      : userRole === 'TEACHER'
        ? '/dashboard/teacher'
        : null;

  const headerVisible = disableAutoHide ? true : isHeaderVisible;
  const positionClass = position === 'fixed' ? 'fixed' : 'sticky';

  // Gemeinsame Geometrie für die Icon-Knöpfe rechts (Oktober 2026): 44px
  // Tipp-Ziel (HIG-Minimum, vorher 40px) und dieselbe Klasse wie die Glocke
  // (NotificationBell schreibt dasselbe Literal), damit die Reihe eine Reihe ist.
  const iconBtn = 'flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-surface/50 transition-colors duration-fast hover:bg-surface/80';

  return (
    <>
      {/* iOS Safe Area: Glassmorphism cover behind notch/Dynamic Island.
          data-app-header-glass: In der iOS-Web-App (Home-Bildschirm) wird das
          Glas deckend, siehe index.css „iOS 26: Unschaerfe am oberen Rand". */}
      <div
        data-app-header-glass=""
        className="fixed top-0 left-0 right-0 z-50 pointer-events-none bg-surface/70 backdrop-blur-xl"
        style={{
          height: 'env(safe-area-inset-top, 0px)'
        }}
      />

      {/* Modern Glassmorphism Header with Auto-Hide */}
      <header
        // Marker fuer index.css: nimmt diesen Header aus der generischen
        // `.min-h-screen > .sticky.top-0`-Safe-Area-Regel heraus (sonst doppelte Polsterung).
        data-app-header=""
        className={`${positionClass} top-0 left-0 right-0 z-50 transition-transform duration-base ease-in-out ${
          headerVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
        style={{ top: `calc(env(safe-area-inset-top, 0px) + ${topOffset}px)` }}
      >
        {/* Blur Background */}
        <div data-app-header-glass="" className="absolute inset-0 bg-surface/70 backdrop-blur-xl border-b border-border/50"></div>

        {/* Header Content */}
        <div
          // data-app-header-inner: Ab 600px rueckt der Kopfinhalt um die Breite
          // der seitlichen Navigation ein (Regel in index.css). Das erledigt
          // zwei Dinge auf einmal — der Avatar steht buendig ueber dem Inhalt
          // statt an der Fensterkante, und er liegt nicht mehr unter den
          // Fenster-Knoepfen, die iPadOS 26 oben links ueber jede App legt.
          // py-2.5 statt py-3 (Oktober 2026): Die Knöpfe rechts sind jetzt 44px
          // hoch statt 40px. Mit dem kleineren Polster bleibt der Kopf genau
          // 64px hoch wie vorher — MainScreen, die klebenden Leisten und die
          // Match-Karte rechnen mit dieser Höhe (--mobile-header-height).
          data-app-header-inner
          className="relative px-4 md:px-6 py-2.5 flex items-center justify-between"
          style={isNativeAndroid ? { paddingTop: '1rem' } : undefined}
        >
          {/* Left: Mini-Profilbild -> Profil/Account-Settings */}
          <div className="flex items-center">
            <button
              onClick={handleAvatarClick}
              className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label="Mein Profil und Einstellungen"
            >
              {/* Ein einziger Platzhalter und ein Rahmen fuer die ganze App:
                  beides bringt ui/Avatar mit. */}
              <Avatar
                name={displayName || combinedUser.email || ''}
                src={avatarUrl}
                size="md"
              />
            </button>
          </div>

          {/* Right: schlanke Icon-Reihe (einheitlich fuer Mobile + Desktop) */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Custom Right Content (z.B. Fehler-beheben-Button) */}
            {rightContent}

            {/* Umschalter App ⇄ Dashboard.
                Im Dashboard MIT Beschriftung: Der Rueckweg war das, was beim
                Onboarding niemand gefunden hat — ein weiteres unbeschriftetes
                Symbol in einer Reihe unbeschrifteter Symbole haette daran nichts
                geaendert. Geometrie wie der Aura-Chip daneben (h-10, px-3), damit
                die Reihe optisch eine Reihe bleibt — aber neutral eingefaerbt:
                Zwei lila Pillen nebeneinander lesen sich als Paar, und lila
                gehoert hier der Marke (Aura) und dem aktiven Ziel. Auffaellig ist
                der Knopf als einziger MIT Text in einer Reihe reiner Symbole.

                Seit Oktober 2026 nur ab md: sichtbar und ohne Haus-Symbol. Auf
                dem Telefon steht derselbe Ausgang beschriftet in der
                Dashboard-Leiste unten (DashboardBottomNav, „App“) und im
                Mehr-Blatt; im Kopf war er dort die dritte Kopie. Ab md: gibt es
                die Leiste nicht, dort bleibt der Knopf der Weg zurück. */}
            {inDashboard ? (
              <button
                onClick={() => navigate('/app')}
                className="hidden md:flex h-11 items-center rounded-xl border border-border bg-surface/50 px-3 whitespace-nowrap transition-colors duration-fast hover:bg-surface/80"
                aria-label="Zurück zur App"
                title="Zurück zur App"
              >
                <span className="text-sm font-semibold text-foreground/80">Zur App</span>
              </button>
            ) : dashboardPath && (
              <button
                onClick={() => navigate(dashboardPath)}
                className={iconBtn}
                aria-label="Dashboard"
                title="Dashboard"
              >
                <LayoutDashboard className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              </button>
            )}

            {/* Aura Points - schmales Chip -> AuraStatsPage.
                Oktober 2026: runde Pille ohne Rand (der feste lila Anker im Kopf,
                eines der höchstens drei lila Elemente), 44px hoch wie die
                Knöpfe daneben. Im Dashboard nicht: Dort ist der Kopf ein
                Werkzeugkopf, und das Lila-Budget gehört dem aktiven Ziel. */}
            {!inDashboard && (
              <button
                onClick={() => navigate('/aura-stats')}
                className="flex h-11 items-center gap-1.5 rounded-full bg-primary-soft px-3 text-sm font-semibold text-primary tabular-nums transition-colors duration-fast hover:bg-primary-soft/80"
                aria-label={`Aura Punkte: ${displayAuraPoints}`}
              >
                <AuraIcon className="h-4 w-4" aria-hidden="true" />
                <span>{displayAuraPoints}</span>
              </button>
            )}

            {/* Notification Bell - ganz rechts außen */}
            <NotificationBell />
          </div>
        </div>
      </header>

    </>
  );
};

Header.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.string,
    auraLevel: PropTypes.number,
    auraPoints: PropTypes.number,
    verifiedSkills: PropTypes.arrayOf(PropTypes.string),
    auraStats: PropTypes.shape({
      dailyMinutes: PropTypes.number,
      currentStreak: PropTypes.number,
      verifiedSessions: PropTypes.number,
      loginStreak: PropTypes.number
    })
  }),
  activeSessions: PropTypes.arrayOf(PropTypes.object),
  plannedSessions: PropTypes.arrayOf(PropTypes.object),
  onJoinSession: PropTypes.func,
  completedSessions: PropTypes.arrayOf(PropTypes.object),
  setCurrentView: PropTypes.func,
  rightContent: PropTypes.node,
  subtitle: PropTypes.string,
  disableAutoHide: PropTypes.bool,
  position: PropTypes.oneOf(['sticky', 'fixed']),
  topOffset: PropTypes.number
};

export default Header;
